import { useId, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import {
  Color,
  PIPELINE_STEPS,
  ROTATIONS,
  STEPS_AFTER_CONFIRM,
  Size,
  confirmBlocksPipeline,
  isRotation,
  noConfidentBoundary,
  t,
  type CropCorners,
  type PipelineStep,
  type Rotation,
  type StringKey,
} from '@bms2/shared';
import { AlertBanner, Button } from '../controls';
import { Text } from '../Text';
import styles from './ScanConfirmScreen.module.css';

/**
 * The upload-review screen of the document photo pipeline (piece 9). Rotation
 * and crop are auto-detected and never silently applied: this screen shows the
 * photo at its detected rotation with rotate controls, the four draggable crop
 * corners over it, and the Confirm that releases grayscale and downsample.
 *
 * Where edge detection found no confident boundary the screen states the error
 * and offers the two ways on — Retry, which re-runs detection on the same
 * original, and Adjust Manually, which asks the parent for corner handles to
 * place by hand. Confirm stays refused while the confirmed crop is unset,
 * because the unset confirmation is what holds the pipeline.
 *
 * The detected values arrive as props and the corrections are held here as
 * overrides, so a parent that hands over a fresh detection — a Retry answered,
 * or the manual corners — is shown, and a correction already made survives an
 * unrelated re-render.
 */
export const scanConfirmDocumentedStates = ['detected', 'noBoundary', 'adjusting', 'processing', 'failed'] as const;

export type ScanConfirmState = (typeof scanConfirmDocumentedStates)[number];

export interface ScanConfirmScreenProps {
  imageUrl: string;
  detectedRotation: Rotation;
  detectedCropCorners: CropCorners | null;
  imageSize: { width: number; height: number };
  state: ScanConfirmState;
  /** The pipeline step that failed, named so a failed scan is never a grey smear nobody can explain. */
  failedStep?: string | null;
  onConfirm: (confirmation: { rotation: Rotation; corners: CropCorners }) => void;
  onRetry: () => void;
  onAdjustManually: () => void;
}

const STEP_LABELS: Record<PipelineStep, StringKey> = {
  grossOrientation: 'scan.step.grossOrientation',
  fineDeskew: 'scan.step.fineDeskew',
  autoCrop: 'scan.step.autoCrop',
  grayscale: 'scan.step.grayscale',
  downsample: 'scan.step.downsample',
};

/** The four corners in drawing order, so a handle's label says which one it is. */
const CORNER_LABELS: readonly StringKey[] = ['scan.corner.topLeft', 'scan.corner.topRight', 'scan.corner.bottomRight', 'scan.corner.bottomLeft'];

/** One arrow keystroke moves a handle this many image pixels. */
const ARROW_STEP = 8;

const ARROW_MOVES: Readonly<Record<string, readonly [number, number]>> = {
  ArrowLeft: [-ARROW_STEP, 0],
  ArrowRight: [ARROW_STEP, 0],
  ArrowUp: [0, -ARROW_STEP],
  ArrowDown: [0, ARROW_STEP],
};

/** A quarter turn along the declared rotations, either way, wrapping at both ends. */
export function stepRotation(rotation: Rotation, direction: 1 | -1): Rotation {
  const index = ROTATIONS.indexOf(rotation);
  const next = ROTATIONS[(((index === -1 ? 0 : index) + direction) % ROTATIONS.length + ROTATIONS.length) % ROTATIONS.length];
  return isRotation(next) ? next : 0;
}

function clamp(value: number, high: number): number {
  return Math.max(0, Math.min(high, value));
}

function moveCorner(corners: CropCorners, index: number, to: readonly [number, number], size: { width: number; height: number }): CropCorners {
  const moved = corners.map((corner, i) => (i === index ? ([clamp(to[0], size.width), clamp(to[1], size.height)] as const) : corner));
  return moved as unknown as CropCorners;
}

export function ScanConfirmScreen({
  imageUrl,
  detectedRotation,
  detectedCropCorners,
  imageSize,
  state,
  failedStep = null,
  onConfirm,
  onRetry,
  onAdjustManually,
}: ScanConfirmScreenProps) {
  const cropHelpId = useId();
  const [rotationCorrection, setRotationCorrection] = useState<Rotation | null>(null);
  const [cornerCorrection, setCornerCorrection] = useState<CropCorners | null>(null);
  const plate = useRef<HTMLDivElement>(null);

  const rotation = rotationCorrection ?? detectedRotation;
  const corners = cornerCorrection ?? detectedCropCorners;
  const busy = state === 'processing';
  const failed = state === 'failed';
  const missingBoundary = noConfidentBoundary(corners, imageSize.width, imageSize.height);
  const showsBoundaryError = state === 'noBoundary' || (state === 'detected' && missingBoundary);
  const blocked = confirmBlocksPipeline({ confirmedRotation: rotation, confirmedCropCorners: corners });
  const confirmDisabled = blocked || busy || failed;

  const rotationLabel = `${rotation}°`;
  const failedName = PIPELINE_STEPS.find((step) => step === failedStep) ?? null;
  const failedLabel = `${t('scan.failedStep')}: ${failedName ? t(STEP_LABELS[failedName]) : (failedStep ?? '')}`;

  const rotate = (direction: 1 | -1) => setRotationCorrection(stepRotation(rotation, direction));

  // Retry re-runs detection on the same original, so the corrections made
  // against the guess it replaces are dropped with it.
  const retry = () => {
    setRotationCorrection(null);
    setCornerCorrection(null);
    onRetry();
  };

  const confirm = () => {
    if (confirmDisabled || corners === null) return;
    onConfirm({ rotation, corners });
  };

  /**
   * Where the pointer is in the photo's own pixels. The plate carries the
   * rotation, so the pointer offset from its centre is turned back through the
   * same angle before it is read as a coordinate: a handle dragged on a
   * sideways photo follows the pointer rather than the screen's axes.
   */
  const pointerToImage = (event: PointerEvent<HTMLButtonElement>): readonly [number, number] | null => {
    const el = plate.current;
    if (!el) return null;
    const box = el.getBoundingClientRect();
    const width = el.offsetWidth;
    const height = el.offsetHeight;
    if (width === 0 || height === 0) return null;
    const dx = event.clientX - (box.left + box.width / 2);
    const dy = event.clientY - (box.top + box.height / 2);
    const radians = (-rotation * Math.PI) / 180;
    const localX = dx * Math.cos(radians) - dy * Math.sin(radians);
    const localY = dx * Math.sin(radians) + dy * Math.cos(radians);
    return [((localX + width / 2) / width) * imageSize.width, ((localY + height / 2) / height) * imageSize.height];
  };

  const onHandleMove = (event: PointerEvent<HTMLButtonElement>, index: number) => {
    if (corners === null || busy || event.buttons === 0) return;
    const to = pointerToImage(event);
    if (to === null) return;
    setCornerCorrection(moveCorner(corners, index, to, imageSize));
  };

  const onHandleKey = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const move = ARROW_MOVES[event.key];
    if (!move || corners === null || busy) return;
    event.preventDefault();
    const from = corners[index]!;
    setCornerCorrection(moveCorner(corners, index, [from[0] + move[0], from[1] + move[1]], imageSize));
  };

  return (
    <section className={styles.screen} data-scan-state={state} data-scan-rotation={rotation} aria-label={t('scan.title')}>
      <Text as="h2" variant="subtitle">{t('scan.title')}</Text>
      <Text as="p" variant="small">{t('scan.intro')}</Text>

      {showsBoundaryError ? (
        <AlertBanner tone="error" message={t('scan.noBoundary')} action={{ label: t('scan.retry'), onAction: retry }} />
      ) : null}
      {failed ? <AlertBanner tone="error" message={failedLabel} action={{ label: t('scan.retry'), onAction: retry }} /> : null}

      <div className={styles.frame}>
        <div className={styles.plate} ref={plate} style={{ transform: `rotate(${rotation}deg)` }} data-scan-plate="true">
          <img className={styles.photo} src={imageUrl} alt={t('scan.photoAlt')} />
          {corners !== null ? (
            <svg className={styles.outline} viewBox={`0 0 ${imageSize.width} ${imageSize.height}`} preserveAspectRatio="none" aria-hidden="true">
              <polygon className={styles.polygon} points={corners.map((c) => `${c[0]},${c[1]}`).join(' ')} />
            </svg>
          ) : null}
          {corners?.map((corner, index) => (
            <button
              key={CORNER_LABELS[index]}
              type="button"
              className={styles.handle}
              style={{ left: `${(corner[0] / imageSize.width) * 100}%`, top: `${(corner[1] / imageSize.height) * 100}%` }}
              aria-label={t(CORNER_LABELS[index]!)}
              aria-describedby={cropHelpId}
              data-scan-corner={index}
              data-scan-corner-x={Math.round(corner[0])}
              data-scan-corner-y={Math.round(corner[1])}
              disabled={busy}
              onKeyDown={(event) => onHandleKey(event, index)}
              onPointerDown={(event) => event.currentTarget.setPointerCapture(event.pointerId)}
              onPointerMove={(event) => onHandleMove(event, index)}
            />
          ))}
        </div>
      </div>

      <div className={styles.controls}>
        <span className={styles.reading}>
          <Text variant="caption">{t('scan.rotation')}</Text>
          <Text variant="small">{rotationLabel}</Text>
        </span>
        <span data-scan-rotate="left">
          <Button size={Size.Medium} text={[t('scan.rotateLeft')]} actions={[() => rotate(-1)]} disabled={busy} />
        </span>
        <span data-scan-rotate="right">
          <Button size={Size.Medium} text={[t('scan.rotateRight')]} actions={[() => rotate(1)]} disabled={busy} />
        </span>
        {showsBoundaryError ? (
          <span data-scan-adjust="true">
            <Button size={Size.Medium} color={Color.Inactive} text={[t('scan.adjustManually')]} actions={[onAdjustManually]} disabled={busy} />
          </span>
        ) : null}
        <span data-scan-confirm="true">
          <Button size={Size.Large} text={[t('scan.confirm')]} actions={[confirm]} disabled={confirmDisabled} />
        </span>
      </div>

      {/* 🔒 The handles' keyboard instructions are their spoken description and never a line on screen (decision 1323); a missing crop still says why Confirm waits. */}
      {corners === null ? <Text as="p" variant="caption">{t('scan.confirmBlocked')}</Text> : null}
      <p id={cropHelpId} className={styles.spoken} data-scan-crop-help="true">{t('scan.cropHelp')}</p>

      {busy ? (
        <div className={styles.steps} data-scan-steps="true">
          <Text as="p" variant="caption">{t('scan.processing')}</Text>
          <ul className={styles.stepList}>
            {STEPS_AFTER_CONFIRM.map((step) => (
              <li key={step} data-scan-step={step}>
                <Text variant="small">{t(STEP_LABELS[step])}</Text>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}
